import { useEffect, useRef } from 'react';

/**
 * Shared live-session sync plumbing for the vocab activity components.
 *
 * ADR 0001 Phase 0. `OnboardingActivity` and `ReviewActivity` each used to
 * re-implement the same five live-sync surfaces plus the observer fieldset
 * lock — ~8 paired edit sites that had to change in lock-step. This hook owns
 * the GENERIC mechanics once; each component injects its own state via
 * closures and opaque payloads, so the hook never enumerates field names.
 *
 * What lives here (generic):
 *   - peer_intent apply        — dispatch teacher directives to a component handler
 *   - remoteState pull         — fan incoming peer state into local setters
 *   - progress_state broadcast — ref-stable onStateChange on a hand-tuned deps list
 *   - stepSignal handler       — teacher Step forward/back
 *   - Enter-to-Continue        — keyboard shortcut
 *   - observer fieldset lock   — the disabled/pointer-events className
 *
 * What stays in the component (type/stage-specific):
 *   - all stage/format useState
 *   - the applyRemoteState / applyPeerIntent fan-out + action bodies
 *   - the broadcastState object + its broadcastDeps array (kept in lock-step)
 *   - handleContinue / advance and onStepBack bodies
 *   - all question rendering and submit handlers
 *
 * @param {Object}   o
 * @param {boolean}  o.interactive       student (true) vs teacher observer (false)
 * @param {boolean} [o.readonly=false]   onboarding's frozen past-step view
 * @param {Object}   o.remoteState       { id, ...fields } peer broadcast | null
 * @param {Function} o.applyRemoteState  (remoteState) => void — per-field setX fan-out
 * @param {Object}   o.peerIntent        { action, payload, id } | null
 * @param {Function} o.applyPeerIntent   (action, payload) => void — action switch
 * @param {Function} o.onStateChange     parent broadcast fn (fresh identity each render)
 * @param {Object}   o.broadcastState    snapshot the component wants broadcast
 * @param {Array}    o.broadcastDeps     the hand-tuned deps that gate the broadcast
 * @param {Object}   o.stepSignal        { direction, ts } | null
 * @param {boolean}  o.showContinue      whether Continue is visible (drives forward)
 * @param {Function} o.onContinue        component's handleContinue
 * @param {Function} [o.onStepBack]      step-back body (onboarding only; review omits)
 * @param {*}        o.lastResult        kept in the keydown deps for closure freshness
 * @returns {{ fieldsetProps: { disabled: boolean, className: string } }}
 */
export default function useLiveActivitySync({
  interactive,
  readonly = false,
  remoteState,
  applyRemoteState,
  peerIntent,
  applyPeerIntent,
  onStateChange,
  broadcastState,
  broadcastDeps,
  stepSignal,
  showContinue,
  onContinue,
  onStepBack,
  lastResult,
}) {
  // ── peer_intent apply ────────────────────────────────────────
  // Interactive (student): apply teacher's directives locally. The student is
  // the source of truth, so the regular progress_state broadcast carries the
  // result back to the teacher (single owner of activity state).
  useEffect(() => {
    if (!interactive || !peerIntent) return;
    const { action, payload } = peerIntent;
    if (!action) return;
    applyPeerIntent(action, payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactive, peerIntent?.id]);

  // ── remoteState pull ─────────────────────────────────────────
  // Observer (teacher): mirror state from peer broadcasts. Guarded on the
  // synthetic `id` the parent stamps so unrelated re-renders (cursor moves)
  // don't re-fire. The closure is rebuilt each render, so the latest
  // applyRemoteState / interactive are used when `id` changes.
  useEffect(() => {
    if (interactive || !remoteState) return;
    applyRemoteState(remoteState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteState?.id]);

  // ── progress_state broadcast ─────────────────────────────────
  // Interactive: notify the parent on every meaningful change so the peer's
  // observer copy stays in sync. Ref indirection keeps `onStateChange`
  // (a fresh inline fn from the parent each render) out of the deps list, so
  // the broadcast fires only when `broadcastDeps` actually changes.
  const onStateChangeRef = useRef(onStateChange);
  useEffect(() => {
    onStateChangeRef.current = onStateChange;
  }, [onStateChange]);
  useEffect(() => {
    if (!interactive || typeof onStateChangeRef.current !== 'function') return;
    onStateChangeRef.current(broadcastState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, broadcastDeps);

  // ── stepSignal (teacher Step forward / back) ─────────────────
  // Only the interactive (student) copy acts; it re-broadcasts the result.
  //   forward — Continue (if visible) else click the active Submit button.
  //   back    — component-supplied onStepBack (onboarding re-renders the prior
  //             step read-only; review omits onStepBack → back is a no-op).
  const lastStepRef = useRef(0);
  useEffect(() => {
    if (!interactive || !stepSignal) return;
    if (stepSignal.ts === lastStepRef.current) return;
    lastStepRef.current = stepSignal.ts;
    if (stepSignal.direction === 'back') {
      if (typeof onStepBack === 'function') onStepBack();
      return;
    }
    if (stepSignal.direction !== 'forward') return;
    if (showContinue) {
      onContinue();
      return;
    }
    // Click the visible Submit button. Defer one tick so we don't run during render.
    setTimeout(() => {
      const btn = document.querySelector(
        '[data-activity-root] form button[type="submit"]:not([disabled])'
      );
      if (btn) btn.click();
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepSignal?.ts, interactive, showContinue]);

  // ── Enter-to-Continue ────────────────────────────────────────
  // Advance when Continue is visible, ignoring Enter while focus is on a real
  // input (cloze typing, sentence-writing textarea). Observer (!interactive)
  // never gets the listener — pressing Enter must not mutate the teacher's
  // local mirror (ADR 0001 R3: aligns onboarding to review's stricter guard).
  // `lastResult` stays in the deps so the listener re-attaches and `onContinue`
  // closes over the latest result.
  useEffect(() => {
    if (!showContinue || !interactive) return;
    const onKey = (e) => {
      if (e.key !== 'Enter' || e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = e.target?.tagName;
      const editable = e.target?.isContentEditable;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || editable) return;
      e.preventDefault();
      onContinue();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showContinue, interactive, lastResult]);

  // ── observer fieldset lock ───────────────────────────────────
  // We deliberately do NOT use a disabled <fieldset> for the live observer: a
  // disabled fieldset disables EVERY descendant with no way to opt back in,
  // which swallowed the teacher's cloze-clue toggle. pointer-events-none on the
  // wrapper blocks the answer controls while letting peer-driven affordances
  // (the clue toggle) re-enable via .pointer-events-auto. A readonly past-step
  // view (onboarding) stays fully `disabled` — it's a frozen historical view.
  const fieldsetProps = {
    disabled: readonly,
    className: [
      'border-0 p-0 m-0 min-w-0',
      !interactive || readonly ? 'opacity-95' : '',
      !interactive && !readonly ? 'pointer-events-none' : '',
    ]
      .filter(Boolean)
      .join(' '),
  };

  return { fieldsetProps };
}
